// §4.5 Count Review (office half; the floor WORKSHEET is the next slice).
//
// Variance table (location · product · unit · expected · counted · Δ qty ·
// Δ value) with per-line accept / reject / recount, a totals banner, and the
// Approve button that posts.
//
// TWO §7 rules meet on this screen and neither is client-side:
//   * Δ VALUE is money — the server sends `variance_value_minor: null` with a
//     `redacted: true` marker for callers without `inventory.cost.read`. The
//     column is rendered only when this page actually holds a visible value
//     (lib/cost.ts); otherwise it is replaced by an honest one-line note. A
//     storekeeper-with-review therefore reviews quantities, never a column of
//     lock glyphs.
//   * EXPECTED is omitted entirely (field ABSENT, never null) while a blind
//     count is in progress and the caller lacks `inventory.count.review`. The
//     column follows the same presence rule: absent field ⇒ no column.
//
// Approve renders the D-049 LOCAL-TIER decision INLINE, because Module 12's
// chain is not bound: `posted` (movements posted), `escalated` (above the
// caller's tier → pending_approval, and `approval_request_id` is null because a
// chain id is never fabricated), or `moved_line_ids` (§9.11 — stock moved since
// the snapshot, those lines went back to recount and NOTHING was posted).

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
// The register's labelled-control Field, aliased: this area's own `Field` is
// the dt/dd detail row from inventory-ui.
import { Field as FormField } from "../../../../components/field";
import { CardSkeleton, EmptyState, ErrorState } from "../../../../components/states";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { hasLiteralRole } from "../../../../lib/role-codes";
import { useTerm } from "../../../onboarding/hooks/terminology";
import {
  Field,
  InventoryStatus,
  MoneyCell,
  MoneyColumnNote,
  RefLabel,
} from "../components/inventory-ui";
import {
  useApproveCount,
  useCancelCycleCount,
  useCycleCount,
  useLocationPathIndex,
  usePrincipalIndex,
  useProductIndex,
  useReviewCountLine,
  useStockUnitIndex,
  type CycleCountLine,
} from "../hooks/inventory-queries";
import { useInventoryPermissions } from "../hooks/permissions";
import { costPresence, showCostColumn } from "../lib/cost";
import { inventoryErrorMessage } from "../lib/errors";
import { formatTimestamp, parseMeasure, shortId, formatMeasureWithUom } from "../lib/measure";

/** |Δ| as a scaled integer, for ordering the review queue by what matters. */
function varianceMagnitude(line: CycleCountLine): bigint {
  const value = parseMeasure(line.variance_qty_base);
  if (value === undefined) return 0n;
  return value < 0n ? -value : value;
}

export function CountReviewScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { term } = useTerm();
  const perms = useInventoryPermissions();
  const params = useParams({ strict: false });
  const id = typeof params.id === "string" ? params.id : "";

  const detail = useCycleCount(id);
  const review = useReviewCountLine();
  const approve = useApproveCount();
  const cancel = useCancelCycleCount();
  const [rejectingLine, setRejectingLine] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState("");
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const lines = useMemo(() => detail.data?.lines ?? [], [detail.data]);

  // The variance rows are what this screen RENDERS, so they are what decides
  // the money column (a non-displayed zero-variance line carrying a visible 0
  // used to keep a column that every displayed row would fill with lock
  // glyphs) — and they are what the reference indexes resolve, so a 400-line
  // count no longer fires ~800 parallel reads before the table paints.
  const variances = useMemo(
    () =>
      lines
        .filter((line) => hasVariance(line))
        .slice()
        .sort((a, b) => {
          const left = varianceMagnitude(a);
          const right = varianceMagnitude(b);
          // A total order, not a two-way guess: equal magnitudes must compare
          // equal or the browser sort is free to reorder them arbitrarily.
          if (left === right) return 0;
          return right > left ? 1 : -1;
        }),
    [lines],
  );

  const products = useProductIndex(variances.map((line) => line.product_id));
  const units = useStockUnitIndex(
    variances.flatMap((line) => (line.stock_unit_id !== null ? [line.stock_unit_id] : [])),
  );
  const principals = usePrincipalIndex(perms.canReadMembers);
  // Exactly the ids this screen LABELS — the count's scope plus the locations of
  // the variance rows it renders. A count over one aisle names a handful of
  // bins; fetching a 200-row page of the facility to find them both cost more
  // and missed the ones past row 200, which rendered as a bare uuid where a
  // reviewer expects a bin path.
  const labelledLocationIds = useMemo(() => {
    const ids = new Set<string>();
    if (detail.data !== undefined) ids.add(detail.data.count.scope_location_id);
    for (const line of variances) ids.add(line.location_id);
    return [...ids];
  }, [detail.data, variances]);
  const locationPath = useLocationPathIndex(labelledLocationIds);

  useEffect(() => {
    if (detail.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [detail.error, navigate]);

  if (detail.isPending || detail.error instanceof UnauthorizedError) {
    return <CardSkeleton lines={6} label={t("inventory.countReview.loading")} />;
  }
  if (detail.error) {
    return (
      <ErrorState
        title={t("inventory.countReview.errorTitle")}
        detail={detail.error.message}
        retryLabel={t("inventory.common.retry")}
        onRetry={() => void detail.refetch()}
        requestId={requestIdOf(detail.error)}
        requestIdLabel={t("inventory.common.requestId")}
      />
    );
  }

  const count = detail.data.count;
  const canReview = perms.has("inventory.count.review");
  const actionable = count.status === "review";
  // §3.4's own table: `cancel` admits EVERY pre-posted state (count-rules.ts —
  // "any pre-posted state → cancelled"), and the API 409s only once the count
  // is posted or already cancelled. The control used to live INSIDE the
  // approve-block (canReview ∧ status ∈ {review, pending_approval}) and was
  // further narrowed to `review`, so a count abandoned at `draft` or halfway
  // through `in_progress` — a bin that turned out to be mid-transfer, a
  // worksheet handed to the wrong shift — could not be closed from any screen
  // by any role, while this file's own comment said a bad count has to be
  // abandonable from the office (surface review F2, 2026-08-12).
  const preposted =
    count.status === "draft" ||
    count.status === "in_progress" ||
    count.status === "review" ||
    count.status === "pending_approval";
  // The server narrows cancel to the admin roles on top of the permission
  // (cycle-counts.service.ts `ADMIN_ROLES`); the mirror says the same thing so
  // the button is not offered to a finance reviewer who would collect a 403.
  const canCancel =
    preposted &&
    canReview &&
    // LITERAL-ROLE-CODES: `cycle-counts.service.ts` cancels for the literal ADMIN_ROLES.
    hasLiteralRole(perms, ["group_admin", "facility_admin"]);

  // §7 presence rules — both columns are decided by what the SERVER sent, over
  // the rows this page actually shows.
  const valuePresence = costPresence(variances, (line) => line.variance_value_minor);
  const withValue = showCostColumn(valuePresence);
  const withExpected = lines.some((line) => line.expected_qty_base !== undefined);
  const totalLines = lines.length;

  // Blind ∧ in_progress ∧ no `count.review` ⇒ the server omits
  // `expected_qty_base` AND nulls `variance_qty_base` (inventory.serializer),
  // so NO line can look like a variance. Claiming "100% accurate" to a caller
  // who is structurally unable to know that — directly under a note saying the
  // expected numbers are hidden — is the screen contradicting itself.
  const varianceUnknowable = !withExpected && totalLines > 0;

  const varianceColumns: DataTableColumn<CycleCountLine>[] = [
    {
      key: "location_id",
      header: term("location"),
      id: true,
      render: (line) => locationPath.pathOf(line.location_id) ?? shortId(line.location_id),
    },
    {
      key: "product_id",
      header: t("inventory.countReview.colProduct"),
      render: (line) => (
        <RefLabel
          value={products.byId.get(line.product_id)?.sku}
          id={line.product_id}
          pending={products.isPending}
          kind="product"
        />
      ),
    },
    {
      key: "stock_unit_id",
      header: term("stock_unit"),
      render: (line) =>
        line.stock_unit_id === null ? (
          "—"
        ) : (
          // Approving posts permanent valued movements; the reviewer must be
          // able to open the unit and see what happened to it since the
          // snapshot.
          <Link to="/inventory/units/$id" params={{ id: line.stock_unit_id }} className="ds-lnk">
            <RefLabel
              value={units.byId.get(line.stock_unit_id)?.serial_code}
              id={line.stock_unit_id}
              pending={units.isPending}
              kind="stock_unit"
            />
          </Link>
        ),
    },
    // §7 presence: the server decides both of these by omitting the key.
    ...(withExpected
      ? [
          {
            key: "expected_qty_base",
            header: t("inventory.countReview.colExpected"),
            numeric: true,
            render: (line: CycleCountLine) =>
              formatMeasureWithUom(line.expected_qty_base, line.uom, { locale: i18n.language }),
          },
        ]
      : []),
    {
      key: "counted_qty_base",
      header: t("inventory.countReview.colCounted"),
      numeric: true,
      render: (line) =>
        line.counted_qty_base === null && line.counted_measure === null
          ? t("inventory.countReview.notCounted")
          : formatMeasureWithUom(line.counted_measure ?? line.counted_qty_base, line.uom, {
              locale: i18n.language,
            }),
    },
    {
      key: "variance_qty_base",
      header: t("inventory.countReview.colDeltaQty"),
      numeric: true,
      render: (line) =>
        line.variance_qty_base === null ? (
          "—"
        ) : (
          <span className={deltaClass(line.variance_qty_base)}>
            {formatMeasureWithUom(line.variance_qty_base, line.uom, { locale: i18n.language })}
          </span>
        ),
    },
    ...(withValue
      ? [
          {
            key: "variance_value_minor",
            header: t("inventory.countReview.colDeltaValue"),
            numeric: true,
            render: (line: CycleCountLine) => (
              <MoneyCell
                row={line}
                valueOf={(row) => row.variance_value_minor}
                currencyOf={() => count.currency}
              />
            ),
          },
        ]
      : []),
    {
      // WHO counted it, WHEN, and what they wrote — without these the approver
      // rubber-stamps or rejects blind.
      key: "counted_by",
      header: t("inventory.countReview.colCountedBy"),
      render: (line) => (
        <>
          <RefLabel
            value={line.counted_by === null ? undefined : principals.byId.get(line.counted_by)}
            id={line.counted_by}
            pending={principals.isPending}
            kind="user"
          />
          {line.counted_at !== null ? (
            <span className="inv-hint"> {formatTimestamp(line.counted_at, i18n.language)}</span>
          ) : null}
          {line.note !== null && line.note.length > 0 ? (
            <span className="count-note"> “{line.note}”</span>
          ) : null}
          {line.recount_of_line_id !== null ? (
            <span className="status-badge status-neutral">
              {t("inventory.countReview.recountOf")}
            </span>
          ) : null}
        </>
      ),
    },
    {
      key: "line_status",
      header: t("inventory.countReview.colStatus"),
      render: (line) => <InventoryStatus status={line.line_status} scope="line" />,
    },
    {
      key: "investigate",
      header: t("inventory.countReview.colInvestigate"),
      render: (line) => (
        <Link
          to="/inventory/movements"
          search={{
            product_id: line.product_id,
            location_id: line.location_id,
            ...(line.stock_unit_id !== null ? { stock_unit_id: line.stock_unit_id } : {}),
          }}
          className="ds-lnk"
        >
          {t("inventory.countReview.openLedger")}
        </Link>
      ),
    },
    ...(canReview && actionable
      ? [
          {
            key: "actions",
            header: t("inventory.countReview.colActions"),
            actions: true,
            render: (line: CycleCountLine) => (
              <>
                <div className="row-actions">
                  <Button
                    data-testid="count-line-accept"
                    disabled={review.isPending && review.variables.lineId === line.id}
                    onClick={() => {
                      review.mutate({ countId: id, lineId: line.id, action: "accept" });
                    }}
                  >
                    {t("inventory.countReview.accept")}
                  </Button>
                  <Button
                    disabled={review.isPending && review.variables.lineId === line.id}
                    onClick={() => {
                      setRejectingLine(line.id);
                      setRejectNote("");
                    }}
                  >
                    {t("inventory.countReview.reject")}
                  </Button>
                  <Button
                    disabled={review.isPending && review.variables.lineId === line.id}
                    onClick={() => {
                      review.mutate({ countId: id, lineId: line.id, action: "recount" });
                    }}
                  >
                    {t("inventory.countReview.recount")}
                  </Button>
                </div>
                {rejectingLine === line.id ? (
                  <form
                    className="inline-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      review.mutate({
                        countId: id,
                        lineId: line.id,
                        action: "reject",
                        note: rejectNote.trim(),
                      });
                      setRejectingLine(null);
                    }}
                  >
                    <FormField label={t("inventory.countReview.rejectNote")}>
                      <input
                        className="ds-input"
                        value={rejectNote}
                        onChange={(event) => {
                          setRejectNote(event.target.value);
                        }}
                        required
                      />
                    </FormField>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={rejectNote.trim().length === 0}
                    >
                      {t("inventory.countReview.confirmReject")}
                    </Button>
                  </form>
                ) : null}
              </>
            ),
          },
        ]
      : []),
  ];

  return (
    <section className="count-review" data-testid="count-review" data-status={count.status}>
      <PageHeader
        crumbs={[{ label: term("cycle_count"), to: "/inventory/counts" }]}
        title={count.count_code}
        actions={
          <>
            <InventoryStatus status={count.status} scope="count" />
          </>
        }
      />

      <dl className="inv-fields">
        <Field label={term("location")}>
          {locationPath.pathOf(count.scope_location_id) ?? shortId(count.scope_location_id)}
        </Field>
        <Field label={t("inventory.counts.colBlind")}>
          {count.blind ? t("inventory.counts.blind") : t("inventory.counts.open")}
        </Field>
        <Field label={t("inventory.countReview.totals")}>
          {t("inventory.countReview.totalsValue", {
            lines: totalLines,
            variances: variances.length,
          })}
        </Field>
      </dl>

      {/* Both notes are guarded on there being lines at all: a count with zero
          lines was asserting that expected values were hidden and that no cost
          was recorded, when nothing had been hidden or withheld. */}
      {!withExpected && totalLines > 0 ? (
        <p className="degraded-note" role="note">
          {t("inventory.countReview.expectedHidden")}
        </p>
      ) : null}
      {totalLines > 0 ? <MoneyColumnNote presence={valuePresence} /> : null}

      {totalLines === 0 ? (
        <EmptyState
          title={t("inventory.countReview.noLinesTitle")}
          body={t("inventory.countReview.noLinesBody")}
        />
      ) : varianceUnknowable ? (
        <EmptyState
          title={t("inventory.countReview.blindPendingTitle")}
          body={t("inventory.countReview.blindPendingBody")}
        />
      ) : variances.length === 0 ? (
        <EmptyState
          title={t("inventory.countReview.emptyTitle")}
          body={t("inventory.countReview.emptyBody")}
        />
      ) : (
        <DataTable
          columns={varianceColumns}
          rows={variances}
          rowKey={(line) => line.id}
          empty={null}
          rowTestId="count-variance-row"
          rowProps={(line) => ({
            "data-line": line.id,
            "data-delta": line.variance_qty_base ?? "",
            "data-value": line.variance_value_minor ?? "",
            "data-redacted": String(line.redacted === true),
          })}
        />
      )}

      {review.isError ? (
        <p className="form-error" role="alert">
          {inventoryErrorMessage(t, review.error, "inventory.countReview.reviewFailed")}
        </p>
      ) : null}

      {canReview && (count.status === "review" || count.status === "pending_approval") ? (
        <div className="approve-block">
          {/* An escalated count is not a disabled button with no explanation:
              the D-049 tier that refused it, and where the decision now lives,
              is stated on load. */}
          {count.status === "pending_approval" ? (
            <p className="degraded-note is-warning" role="note">
              {t("inventory.countReview.pendingApprovalNote")}{" "}
              <Link to="/inventory/approvals">{t("inventory.countReview.openApprovals")}</Link>
            </p>
          ) : confirmApprove ? (
            // Posting writes N permanent count_adjustment movements that can
            // only be undone one reversal at a time — the largest write on the
            // office slice gets the same confirmation the much smaller ledger
            // Reverse already had, and it names what cannot be seen.
            <div className="inline-confirm">
              <p className="inv-warning">
                {t("inventory.countReview.approveConfirm", { n: variances.length })}
              </p>
              {valuePresence !== "visible" ? (
                <p className="inv-hint">
                  {valuePresence === "withheld"
                    ? t("inventory.countReview.approveBlindValue")
                    : t("inventory.countReview.approveUnvalued")}
                </p>
              ) : null}
              <div className="form-actions">
                <Button
                  variant="primary"
                  data-testid="count-approve-confirm"
                  busy={approve.isPending}
                  onClick={() => {
                    approve.mutate({ id });
                    setConfirmApprove(false);
                  }}
                >
                  {t("inventory.countReview.approveConfirmSubmit")}
                </Button>
                <Button
                  onClick={() => {
                    setConfirmApprove(false);
                  }}
                >
                  {t("inventory.common.cancel")}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="primary"
              data-testid="count-approve"
              busy={approve.isPending}
              busyLabel={t("inventory.countReview.approving")}
              onClick={() => {
                setConfirmApprove(true);
              }}
            >
              {t("inventory.countReview.approve")}
            </Button>
          )}

          {/* The D-049 local tier decides today; Module 12's chain takes over
              when its port binds. Whatever happens is stated here, inline. */}
          {approve.isSuccess ? (
            <p
              className="inv-success"
              data-testid="count-approve-result"
              data-posted={String(approve.data.posted)}
              data-escalated={String(approve.data.escalated === true)}
              role="status"
            >
              {approve.data.posted
                ? t("inventory.countReview.posted")
                : approve.data.escalated === true
                  ? t("inventory.countReview.escalated")
                  : (approve.data.moved_line_ids ?? []).length > 0
                    ? t("inventory.countReview.movedLines", {
                        n: (approve.data.moved_line_ids ?? []).length,
                      })
                    : t("inventory.countReview.notPosted")}
              {approve.data.escalated === true ? (
                <span className="inv-hint">
                  {" "}
                  <Link to="/inventory/approvals">{t("inventory.countReview.openApprovals")}</Link>
                </span>
              ) : null}
              {approve.data.approval_request_id === null ? (
                <span className="inv-hint"> {t("inventory.countReview.localTier")}</span>
              ) : (
                <span className="inv-hint">
                  {" "}
                  {t("inventory.countReview.chain", {
                    id: shortId(approve.data.approval_request_id),
                  })}
                </span>
              )}
            </p>
          ) : null}
          {approve.isError ? (
            <p className="form-error" role="alert">
              {inventoryErrorMessage(t, approve.error, "inventory.countReview.approveFailed")}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* §5 row 43 — a bad count has to be abandonable from the office, from
          WHEREVER it went bad. Outside the approve-block on purpose: a draft
          and an in-progress count have nothing to approve and everything to
          abandon, and the state machine admits `cancel` in all four pre-posted
          states. A reason is required here because the server records it on
          the row and "cancelled, no reason" is the audit gap this screen
          exists to close. */}
      {canCancel ? (
        <div className="approve-block" data-testid="count-cancel-block">
          {cancelOpen ? (
            <form
              className="inline-form"
              onSubmit={(event) => {
                event.preventDefault();
                cancel.mutate(
                  { id, reason: cancelReason.trim() },
                  {
                    onSuccess: () => {
                      setCancelOpen(false);
                    },
                  },
                );
              }}
            >
              <FormField label={t("inventory.countReview.cancelReason")}>
                <input
                  className="ds-input"
                  value={cancelReason}
                  data-testid="count-cancel-reason"
                  onChange={(event) => {
                    setCancelReason(event.target.value);
                  }}
                  required
                />
              </FormField>
              <Button
                type="submit"
                data-testid="count-cancel-submit"
                busy={cancel.isPending}
                disabled={cancelReason.trim().length === 0}
              >
                {t("inventory.countReview.cancelSubmit")}
              </Button>
              <Button
                onClick={() => {
                  setCancelOpen(false);
                }}
              >
                {t("inventory.common.cancel")}
              </Button>
            </form>
          ) : (
            <Button
              data-testid="count-cancel"
              onClick={() => {
                setCancelOpen(true);
              }}
            >
              {t("inventory.countReview.cancelCount")}
            </Button>
          )}
          {cancel.isError ? (
            <p className="form-error" role="alert">
              {inventoryErrorMessage(t, cancel.error, "inventory.countReview.cancelFailed")}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/** A line is worth reviewing when it has a non-zero variance or was skipped. */
function hasVariance(line: CycleCountLine): boolean {
  const variance = parseMeasure(line.variance_qty_base);
  if (variance !== undefined && variance !== 0n) return true;
  return line.counted_qty_base === null && line.counted_measure === null;
}

function deltaClass(value: string): string {
  const parsed = parseMeasure(value);
  if (parsed === undefined || parsed === 0n) return "delta";
  return parsed < 0n ? "delta delta-negative" : "delta delta-positive";
}
